import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  // §10 chip: a quiet bg-muted pill, status carried by a leading dot (see
  // StatusBadge), never a fully-saturated neon fill. focus-visible only (§9).
  "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  {
    variants: {
      variant: {
        // The single saturated accent — reserved for the primary action chip.
        default:
          "border border-transparent bg-primary text-primary-foreground hover:bg-primary/90",
        // The §10 default: a calm muted chip; the dot carries the colour.
        secondary: "border border-transparent bg-muted text-foreground",
        // Low-saturation tints (§1.4), not white-on-neon.
        destructive:
          "border border-transparent bg-destructive/10 text-destructive",
        success: "border border-transparent bg-success/10 text-success",
        warning:
          "border border-transparent bg-warning/15 text-warning-foreground",
        // Faded archived/deprecated axis.
        outline: "border border-border text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "secondary",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
